import { Component, effect, inject } from '@angular/core';
import { AnalyticsService } from './services/analytics.service';
import { TrackingLoaderService } from './services/tracking-loader.service';
import { ConsentService } from './services/consent.service';
import { SpinnerOverlayGuardService } from './services/spinner-overlay-guard.service';
import { Meta, DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { TranslateService } from '@ngx-translate/core';
import { registerAppIcons } from './shared/icons/register-app-icons';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  title = 'Reite Store';

  supportLanguages =  ['es', 'en'];

  private readonly tracking = inject(TrackingLoaderService);
  // Injected for its constructor, which applies any stored cookie choice before the first paint.
  // Depending on the banner component to do this would tie a privacy guarantee to a template.
  private readonly consent = inject(ConsentService);
  // Injected so it starts tracking spinners at bootstrap, before anything can show one.
  private readonly spinnerGuard = inject(SpinnerOverlayGuardService);

  constructor(
    private analytics: AnalyticsService,
    private translateService: TranslateService,
    private meta: Meta,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer
  ) {
    this.deferAppOpenUntilConsented();
    this.translateService.addLangs(this.supportLanguages);
    this.translateService.setDefaultLang('es');
    const browserLang = this.translateService.getBrowserLang();
    this.translateService.use(browserLang? browserLang : 'es');
    const themeColor = environment.themeColor;
    this.meta.updateTag({ content: themeColor }, 'name=theme-color');

    registerAppIcons(this.matIconRegistry, this.domSanitizer);
  }

  /**
   * Emit `app_open` once analytics consent is granted and auth state has answered, rather than at
   * construction.
   *
   * The constructor runs before the banner can be shown, and `AnalyticsService` no-ops without
   * consent, so firing there would drop the event for every user — including those who go on to
   * accept. Waiting for the signal keeps the metric intact for consenting users and absent for the
   * rest.
   *
   * Waiting on attribution as well keeps this event comparable with the rest: it is the one event
   * every session emits, so if it alone went out before the uid was known, total sessions and
   * attributed sessions would disagree by construction. "Nobody is signed in" counts as an answer,
   * so a visitor who never signs in still emits it.
   *
   * Fires at most once per page load: this is "the app was opened", not "consent was granted", so
   * a later change in the preferences panel must not re-emit it.
   */
  private deferAppOpenUntilConsented(): void {
    let sent = false;
    const stop = effect(() => {
      if (sent || !this.tracking.analyticsAllowed() || !this.analytics.attributionResolved()) return;
      sent = true;
      this.analytics.logEvent('app_open');
      // Outside the effect's own execution, or Angular reports writing to a signal it depends on.
      queueMicrotask(() => stop.destroy());
    });
  }

}
